import { lazy, Suspense } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAppFlow } from '@/contexts/AppFlowContext';

// Lazy load the heavy component
const AppLaunchOrchestrator = lazy(() => import('@/components/AppLaunchOrchestrator'));

/**
 * AppFlowOverlay renders the launch/profile screens as full-screen overlays
 * without changing the URL. This provides a native app-like experience.
 */
export function AppFlowOverlay() {
  const { currentScreen } = useAppFlow();

  // Don't render anything if no overlay is active
  if (currentScreen === 'none') {
    return null;
  }

  return (
    <AnimatePresence mode="wait">
      {(currentScreen === 'launch' || currentScreen === 'profiles' || currentScreen === 'loading') && (
        <motion.div
          key={currentScreen}
          className="fixed inset-0 z-[9999]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Suspense fallback={<OverlayLoader />}>
            {currentScreen === 'launch' && (
              <AppLaunchOrchestratorWrapper skipVideo={false} />
            )}
            {currentScreen === 'profiles' && (
              <AppLaunchOrchestratorWrapper skipVideo={true} />
            )}
            {currentScreen === 'loading' && (
              <OverlayLoader />
            )}
          </Suspense>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Wrapper that connects AppLaunchOrchestrator to the AppFlow context
 */
function AppLaunchOrchestratorWrapper({ skipVideo }: { skipVideo: boolean }) {
  return <AppLaunchOrchestrator skipVideo={skipVideo} />;
}

/**
 * Simple loading state for the overlay
 */
function OverlayLoader() {
  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center gap-4"
      >
        {/* Animated logo container with spinning ring */}
        <div className="relative w-24 h-24 flex items-center justify-center">
          {/* Spinning gradient ring */}
          <div 
            className="absolute inset-0 rounded-full animate-spin"
            style={{ 
              background: 'conic-gradient(from 0deg, hsl(var(--primary)), hsl(var(--primary) / 0.3), transparent)',
              mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              maskComposite: 'xor',
              WebkitMaskComposite: 'xor',
              padding: '3px',
              animationDuration: '1.2s'
            }} 
          />
          {/* Logo */}
          <img 
            src="/logo.png" 
            alt="BuxBox" 
            className="w-14 h-14 object-contain rounded-xl"
          />
        </div>
        <p className="text-foreground/60 text-sm">Loading...</p>
      </motion.div>
    </div>
  );
}
